import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { AddCircleOutline, Delete, Edit } from "@mui/icons-material";
import { Button, IconButton, Tooltip, Switch, Chip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ShiftApiViewController from "../../redux/actions/shiftApiAction";
import AddShiftForm from "./AddShiftForm";
import ExportButton from "../../components/ag grid/ExportButton";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx } from "../../components/auth/authTheme";
import usePermission from "../../utils/usePermission";

import "../../styles/styles.css";
import "../../styles/layout.css";

const ShiftMasterList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const { hasPermission } = usePermission();

  const [showModal, setShowModal] = useState(false);
  const [editShift, setEditShift] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);

  const { getShifts, deleteShift } = ShiftApiViewController();
  const { shiftsData, loading } = useSelector((state) => state.shifts);

  const canCreate = hasPermission("shift_master", "create");
  const canEdit = hasPermission("shift_master", "edit");
  const canDelete = hasPermission("shift_master", "delete");

  useEffect(() => {
    dispatch(getShifts());
  }, [dispatch]);

  const handleDelete = useCallback(
    (id) => dispatch(deleteShift(id)),
    [dispatch, deleteShift]
  );

  const handleEdit = useCallback((shift) => {
    setEditShift(shift);
    setShowModal(true);
  }, []);

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    const [hours, minutes] = timeString.split(":");
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
      },
      {
        headerName: "SR.No",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Shift Name",
        field: "shift_name",
        flex: 1,
        minWidth: 150,
      },
      {
        headerName: "Start Time",
        field: "start_time",
        flex: 1,
        minWidth: 120,
        valueFormatter: (params) => formatTime(params.value),
      },
      {
        headerName: "End Time",
        field: "end_time",
        flex: 1,
        minWidth: 120,
        valueFormatter: (params) => formatTime(params.value),
      },
      {
        headerName: "Default",
        field: "is_default",
        width: 100,
        cellRenderer: (params) => {
          return params.value ? <Chip label="Default" color="primary" size="small" /> : null;
        }
      },
      {
        headerName: "Active",
        field: "is_active",
        width: 100,
        cellRenderer: (params) => (
          <Switch
            checked={params.value}
            size="small"
            disabled
          />
        ),
      },
      ...(canEdit || canDelete
        ? [
            {
              headerName: "Actions",
              field: "actions",
              pinned: "right",
              width: 120,
              minWidth: 120,
              maxWidth: 120,
              suppressSizeToFit: true,
              sortable: false,
              cellRenderer: (params) => (
                <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
                  {canEdit && (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(params.data)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        sx={{ color: "#d32f2f" }}
                        onClick={() => handleDelete(params.data.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canEdit, canDelete, handleEdit, handleDelete]
  );

  return (
    <ListPageLayout
      title="Shift Master"
      subtitle="Manage organization shifts and timings"
      headerAction={
        canCreate ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={() => {
              setEditShift(null);
              setShowModal(true);
            }}
            sx={toolbarButtonSx}
          >
            Add Shift
          </Button>
        ) : null
      }
      toolbarRight={
        <>
          <ExportButton
            gridRef={gridRef}
            fileName="shifts.xlsx"
            exportColumns={["shift_name", "start_time", "end_time"]}
            disabled={selectedRows.length === 0}
          />
          <PageSizeSelector
            pageSize={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          />
        </>
      }
    >
      <div className="list-page-grid">
        <ReusableGrid
          gridRef={gridRef}
          rowData={shiftsData}
          columnDefs={columnDefs}
          paginationPageSize={pageSize}
          loading={loading}
          onSelectionChanged={(rows) => setSelectedRows(rows)}
        />
      </div>

      <AddShiftForm
        open={showModal}
        handleClose={() => setShowModal(false)}
        editData={editShift}
      />
    </ListPageLayout>
  );
};

export default ShiftMasterList;
